import { Box, Typography, IconButton, useMediaQuery, useTheme } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { TournamentBracketMatchGameStatus, TournamentStatus } from '@okletsplay/core';
import { DateFormat, ModalName, Route } from 'app/enums';
import { BracketSeedMatchGameEntity, TournamentTeamEntity } from 'app/interfaces/entity';
import { modalsReducers } from 'app/store/modal/slice';
import { BracketSeedStatus } from 'modules/bracket/components/BracketSeedStatus';
import { PlayoffGameChip } from 'modules/bracket/components/PlayoffGameChip';
import moment from 'moment';
import React, { FC, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TournamentMatchGameScore } from '../TournamentMatchGameScore';
import { useStyles } from './TournamentMatchGameCard.styles';

interface TournamentMatchGameCardProps {
  matchGame: BracketSeedMatchGameEntity;
  tournamentStatus: TournamentStatus;
  teams: TournamentTeamEntity[];
  gameIcon: string;
}

export const TournamentMatchGameCard: FC<TournamentMatchGameCardProps> = ({
  matchGame,
  gameIcon,
  tournamentStatus,
  teams,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const isLessThanXxs = useMediaQuery(theme.breakpoints.down('sm'));
  const isLessThanMd = useMediaQuery(theme.breakpoints.down('md'));

  const title = useMemo(() => `Game ${Number(matchGame.number)}`, [matchGame]);

  const startedAtLabel = useMemo(() => {
    if (!matchGame?.startedAt) {
      return null;
    }

    return `Started at ${moment(matchGame.startedAt).format(DateFormat.YyyyMmDdHhMm)}`;
  }, [matchGame?.startedAt]);

  const finishedAtLabel = useMemo(() => {
    if (!matchGame?.finishedAt) {
      return null;
    }

    return `Finished at ${moment(matchGame.finishedAt).format(DateFormat.YyyyMmDdHhMm)}`;
  }, [matchGame?.finishedAt]);

  const fullDateLabel = useMemo<string>(() => {
    if (startedAtLabel && finishedAtLabel) {
      return `${startedAtLabel} • ${finishedAtLabel}`;
    }

    return startedAtLabel && !finishedAtLabel ? startedAtLabel : '';
  }, [finishedAtLabel, startedAtLabel]);

  const viewGameStatisticsHandler = useCallback(
    (id?: string) => {
      if (!id) {
        return;
      }
      dispatch(modalsReducers.closeModal(ModalName.TournamentMatchDetails));
      navigate(Route.GameStatistics.replace(':id', id ?? ''));
    },
    [dispatch, navigate]
  );

  const dateTemplate = useMemo(() => {
    if (!startedAtLabel && !finishedAtLabel) {
      return null;
    }

    if (isLessThanMd) {
      return (
        <Box pt={1} pb={1}>
          <Typography className={classes.date}>{startedAtLabel}</Typography>
          <Typography className={classes.date}>{finishedAtLabel}</Typography>
        </Box>
      );
    }

    return (
      <Typography pt={1} pb={1} className={classes.date}>
        {fullDateLabel}
      </Typography>
    );
  }, [classes.date, isLessThanMd, fullDateLabel, startedAtLabel, finishedAtLabel]);

  return (
    <Box className={classes.root}>
      <Box className={classes.match}>
        {!isLessThanXxs && (
          <Box className={classes.icon}>
            <img src={gameIcon} alt="game-icon" />
          </Box>
        )}
        <Box>
          <Box className={classes.titleContainer}>
            <Typography pr={1}>{title}</Typography>
            {matchGame?.gameStatistics && (
              <IconButton
                aria-label="view results"
                className={classes.goToGameButton}
                onClick={() => viewGameStatisticsHandler(matchGame.gameStatistics)}
              >
                <OpenInNewIcon />
              </IconButton>
            )}
          </Box>
          {dateTemplate}
          <Box>
            {matchGame && (
              <BracketSeedStatus
                matchStatus={matchGame.status as TournamentBracketMatchGameStatus}
                tournamentStatus={tournamentStatus}
              />
            )}
            {matchGame.isPlayoffGame && (
              <Box pl={1} display="inline">
                <PlayoffGameChip />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box className={classes.scoreContainer}>
        <TournamentMatchGameScore
          teams={teams}
          results={matchGame.matchGameResults}
          resultReason={matchGame.resultReason}
        />
      </Box>
    </Box>
  );
};
